// src/components/ai/copilot/AiCopilotPanel.js
//
// Unified surface for all instructor copilot draft types.
// Reads DRAFT_TYPES config for the given draftType and renders the correct
// two-phase UI (input → review/apply) via useInstructorCopilot.
//
// surface: 'modal'  — input in a small centered modal; review in AiDraftReviewModal
// surface: 'panel'  — both phases rendered inline
//
// reviewMode: 'text'    — single editable textarea
// reviewMode: 'fields'  — main text + labeled editable fields
// reviewMode: 'custom'  — structured display (currently: lesson_plan)

import React, { useEffect, useCallback } from 'react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useInstructorCopilot } from '../../../hooks/useInstructorCopilot';
import { useInstructorAiProfile } from '../../../hooks/useInstructorAiProfile';
import { getDraftTypeConfig } from '../../../utils/copilotDraftTypes';
import InstructorQuickAiDraftInput from './InstructorQuickAiDraftInput';
import AiDraftReviewModal from './AiDraftReviewModal';
import AiGeneratedDraftEditor from './AiGeneratedDraftEditor';
import AiDraftContextSummary from './AiDraftContextSummary';
import AiAttributionBadge from '../AiAttributionBadge';

// ─────────────────────────────────────────────────────────────
// Internal: close-button SVG (shared across shells)
// ─────────────────────────────────────────────────────────────

function CloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
    >
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Internal: panel review action bar
// ─────────────────────────────────────────────────────────────

function PanelActionBar({ onDiscard, onRegenerate, onAccept, applying, generating, applyLabel, canAccept }) {
  return (
    <div className="mt-4 flex flex-wrap justify-end gap-3 border-t border-violet-100 pt-4">
      <button
        type="button"
        onClick={onDiscard}
        disabled={applying}
        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        Discard
      </button>
      {onRegenerate ? (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={applying || generating}
          className="rounded-full border border-violet-300 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
        >
          {generating ? 'Regenerating…' : 'Regenerate'}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onAccept}
        disabled={!canAccept || applying}
        className="rounded-full bg-slate-950 px-5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {applying ? 'Applying…' : applyLabel}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Internal: custom review renderer for lesson_plan
// ─────────────────────────────────────────────────────────────

function LessonPlanReview({ aiResult, onAccept, onRegenerate, onDiscard, applying, generating, applyLabel }) {
  const hasResult = aiResult && (aiResult.stages?.length > 0 || aiResult.focus || aiResult.notes);
  if (!hasResult) return null;

  return (
    <div className="space-y-3">
      {aiResult.focus ? (
        <div>
          <p className="text-xs font-semibold text-violet-600">Suggested focus</p>
          <p className="mt-1 text-sm text-slate-700">{aiResult.focus}</p>
        </div>
      ) : null}

      {aiResult.stages?.length > 0 ? (
        <div>
          <p className="text-xs font-semibold text-violet-600">Suggested stages</p>
          <div className="mt-2 space-y-2">
            {aiResult.stages.map((stage, index) => (
              <div key={stage.stage || index} className="rounded-xl bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {stage.stage || `Stage ${index + 1}`}
                </p>
                {stage.coachNotes ? (
                  <p className="mt-1 text-sm text-slate-700">{stage.coachNotes}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {aiResult.drillSuggestions?.length > 0 ? (
        <div>
          <p className="text-xs font-semibold text-violet-600">Suggested drills</p>
          <ul className="mt-1 space-y-1">
            {aiResult.drillSuggestions.map((drill, index) => (
              <li key={index} className="text-sm text-slate-700">— {drill}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {aiResult.notes ? (
        <div>
          <p className="text-xs font-semibold text-violet-600">Additional notes</p>
          <p className="mt-1 text-sm text-slate-700">{aiResult.notes}</p>
        </div>
      ) : null}

      <p className="text-xs text-slate-400">AI suggestion — review carefully before applying.</p>

      <PanelActionBar
        onDiscard={onDiscard}
        onRegenerate={onRegenerate}
        onAccept={onAccept}
        applying={applying}
        generating={generating}
        applyLabel={applyLabel}
        canAccept
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Internal: fire-and-forget tone frequency write-back
// ─────────────────────────────────────────────────────────────

function writeToneFrequency(instructorId, tone) {
  if (!instructorId || !tone) return;
  const ref = doc(db, 'instructorAiProfiles', instructorId);
  updateDoc(ref, { [`derived.toneFrequency.${tone}`]: increment(1) }).catch(() => {});
}

/**
 * Weighted rolling average for edit distance.
 * new = (existing * weight + newValue) / (weight + 1)
 */
function rollingAvgEditDistance(existing, newValue, weight = 9) {
  if (existing === null || existing === undefined) return newValue;
  return (existing * weight + newValue) / (weight + 1);
}

function writeEditDistance(instructorId, ratio, existingAvg) {
  if (!instructorId || ratio === null || ratio === undefined) return;
  const newAvg = rollingAvgEditDistance(existingAvg, ratio);
  const ref = doc(db, 'instructorAiProfiles', instructorId);
  updateDoc(ref, { 'derived.avgDraftEditDistance': newAvg }).catch(() => {});
}

// ─────────────────────────────────────────────────────────────
// AiCopilotPanel
// ─────────────────────────────────────────────────────────────

/**
 * Unified copilot surface. Reads DRAFT_TYPES[draftType] for UI config.
 *
 * Props:
 *   draftType          — string — key into DRAFT_TYPES
 *   signals            — object — pre-built client signals (caller's responsibility)
 *   applyFn            — async (payload) => void
 *   instructorId       — string — used to load and write back the AI profile
 *   clientName         — string — shown in modal header
 *   availableTags      — [{ key, label }] — for showTagChips types
 *   initialQuickInput  — object — pre-fills InstructorQuickAiDraftInput
 *   onDone             — () => void — called when phase === 'done'
 *   onDiscard          — () => void — called on discard / close
 */
function AiCopilotPanel({
  draftType,
  signals,
  applyFn,
  instructorId = null,
  clientName = '',
  availableTags = [],
  initialQuickInput = {},
  onDone,
  onDiscard,
}) {
  const config = getDraftTypeConfig(draftType);

  // Load the instructor's AI profile (non-blocking)
  const { profile: instructorProfile } = useInstructorAiProfile(instructorId);

  // Called by useInstructorCopilot on accept — fire-and-forget write-back
  const handleEditDistance = useCallback((ratio) => {
    writeEditDistance(instructorId, ratio, instructorProfile?.derived?.avgDraftEditDistance ?? null);
  }, [instructorId, instructorProfile]);

  const {
    phase,
    generating,
    applying,
    draftText,
    originalAiDraft,
    editableFields,
    aiResult,
    generateError,
    applyError,
    handleGenerate,
    handleRegenerate,
    handleDraftChange,
    handleFieldChange,
    handleAccept,
    handleDiscard,
  } = useInstructorCopilot({
    draftType,
    signals,
    applyFn,
    instructorProfile,
    onEditDistance: handleEditDistance,
  });

  // Notify parent when done
  useEffect(() => {
    if (phase === 'done') onDone?.();
  }, [phase, onDone]);

  const handleDiscardAndClose = () => {
    handleDiscard();
    onDiscard?.();
  };

  // Wraps handleGenerate to fire tone write-back before delegating
  const handleGenerateWithToneTracking = useCallback((quickInput, contextClause) => {
    if (quickInput?.tone) {
      writeToneFrequency(instructorId, quickInput.tone);
    }
    handleGenerate(quickInput, contextClause);
  }, [instructorId, handleGenerate]);

  const inInputPhase = phase === 'idle' || phase === 'generating';
  const inReviewPhase = phase === 'review' || phase === 'applying';

  // ── Quick input sub-component ────────────────────────────
  const quickInputEl = (
    <>
      {generateError ? (
        <p className="mb-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          {generateError}
        </p>
      ) : null}
      <InstructorQuickAiDraftInput
        onGenerate={handleGenerateWithToneTracking}
        loading={generating}
        generateLabel={config.generateLabel}
        placeholder={config.placeholder}
        showSkillPicker={config.showSkillPicker}
        showDurationInput={config.showDurationInput}
        showTonePicker={config.showTonePicker}
        showTagChips={config.showTagChips}
        availableTags={availableTags}
        initialQuickInput={initialQuickInput}
        signalOnlyWarning={config.signalOnlyWarning}
      />
    </>
  );

  // ── Modal surface ─────────────────────────────────────────
  if (config.surface === 'modal') {
    // Input phase: small centered modal
    if (inInputPhase) {
      return (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={config.modalTitle}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={handleDiscardAndClose}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-500">
                  {config.modalTitle}
                </p>
                {clientName ? (
                  <h2 className="mt-1 text-lg font-semibold text-slate-950">{clientName}</h2>
                ) : null}
              </div>
              <CloseButton onClick={handleDiscardAndClose} />
            </div>
            {quickInputEl}
          </div>
        </div>
      );
    }

    // Review phase: delegate to AiDraftReviewModal (it has its own shell)
    if (inReviewPhase && config.reviewMode === 'text') {
      return (
        <AiDraftReviewModal
          title={config.modalTitle}
          clientName={clientName}
          quickInput={null}
          draftText={draftText}
          originalAiDraft={originalAiDraft}
          onDraftChange={handleDraftChange}
          aiResult={aiResult}
          onAccept={handleAccept}
          onDiscard={handleDiscardAndClose}
          onRegenerate={handleRegenerate}
          acceptLabel={config.applyLabel}
          regenerating={generating}
          sending={applying}
          sendError={applyError}
          onClose={handleDiscardAndClose}
        />
      );
    }

    return null;
  }

  // ── Panel surface ─────────────────────────────────────────
  const panelHeader = (
    <p className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-violet-500">
      {config.modalTitle}
    </p>
  );

  if (inInputPhase) {
    return (
      <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4">
        {panelHeader}
        {quickInputEl}
      </div>
    );
  }

  if (inReviewPhase) {
    // ── custom: lesson_plan ──────────────────────────────
    if (config.reviewMode === 'custom') {
      return (
        <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4">
          {panelHeader}
          <LessonPlanReview
            aiResult={aiResult}
            onAccept={handleAccept}
            onRegenerate={() => { handleDiscard(); }}
            onDiscard={handleDiscardAndClose}
            applying={applying}
            generating={generating}
            applyLabel={config.applyLabel}
          />
        </div>
      );
    }

    // ── text / fields ────────────────────────────────────
    const canAccept = Boolean(draftText.trim()) && !applying;

    return (
      <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4">
        {panelHeader}

        {/* What the coach provided — read-only context reminder */}
        <AiDraftContextSummary quickInput={null} compact />

        {/* Main draft text */}
        <div className="mt-3">
          <AiGeneratedDraftEditor
            value={draftText}
            onChange={handleDraftChange}
            hasBeenEdited={draftText !== originalAiDraft && Boolean(originalAiDraft)}
            label={config.reviewMode === 'fields' ? 'Summary — edit before applying' : 'Draft — edit before using'}
            disabled={applying}
          />
        </div>

        {/* Additional editable fields (reviewMode: 'fields') */}
        {config.reviewMode === 'fields' && editableFields?.length > 0 ? (
          <div className="mt-3 space-y-3">
            {editableFields.map((field) => (
              <div key={field.key}>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {field.label || field.key}
                </label>
                <textarea
                  className="w-full rounded-xl border border-violet-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50"
                  rows={2}
                  value={field.value ?? ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  disabled={applying}
                />
              </div>
            ))}
          </div>
        ) : null}

        {/* Attribution */}
        {aiResult ? (
          <div className="mt-3">
            <AiAttributionBadge
              basedOn={aiResult.basedOn || []}
              confidence={aiResult.confidence || 'low'}
            />
          </div>
        ) : null}

        {applyError ? (
          <p className="mt-2 text-sm text-rose-600">{applyError}</p>
        ) : null}

        <PanelActionBar
          onDiscard={handleDiscardAndClose}
          onRegenerate={handleRegenerate}
          onAccept={handleAccept}
          applying={applying}
          generating={generating}
          applyLabel={config.applyLabel}
          canAccept={canAccept}
        />
      </div>
    );
  }

  return null;
}

export default AiCopilotPanel;
